import { Button, Input } from '@nextui-org/react';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@nextui-org/modal';
import React, { useState } from 'react';

import { PartnerInfo } from '../../types';
import { useAuth0 } from '@auth0/auth0-react';

interface AddPartnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  createAuth0User: (formData: Partial<PartnerInfo>) => Promise<string>;
  refreshPartners: () => void;
}

const AddPartnerModal: React.FC<AddPartnerModalProps> = ({
  isOpen,
  onClose,
  createAuth0User,
  refreshPartners,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  const [formData, setFormData] = useState<Partial<PartnerInfo>>({});
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveClick = async () => {
    if (!formData.email || isLoading) {
      setError('Email is required to create an account.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const auth0Id = await createAuth0User(formData);

      const partnerData = {
        ...formData,
        auth0_id: auth0Id,
        shopify_customer_id: formData.shopify_customer_id || null,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/partner/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(partnerData),
        }
      );

      if (response.ok) {
        await refreshPartners();
      } else {
        throw new Error('Failed to save partner in the database');
      }

      onClose();
    } catch (err) {
      console.error('Error creating partner:', err);
      setError('Failed to create partner.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 pb-2">
          <h3 className="text-lg font-bold text-slate-800">Add New Partner</h3>
          <p className="text-xs text-slate-400 font-normal">
            Create a new authorized partner account
          </p>
        </ModalHeader>
        <ModalBody className="gap-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input
              name="first_name"
              label="First Name"
              variant="bordered"
              radius="sm"
              value={formData.first_name || ''}
              onChange={handleChange}
              classNames={{ label: 'text-slate-500 text-xs' }}
            />
            <Input
              name="last_name"
              label="Last Name"
              variant="bordered"
              radius="sm"
              value={formData.last_name || ''}
              onChange={handleChange}
              classNames={{ label: 'text-slate-500 text-xs' }}
            />
          </div>
          <Input
            name="company_name"
            label="Company Name"
            variant="bordered"
            radius="sm"
            value={formData.company_name || ''}
            onChange={handleChange}
            classNames={{ label: 'text-slate-500 text-xs' }}
          />
          <Input
            name="email"
            label="Email Address"
            variant="bordered"
            radius="sm"
            value={formData.email || ''}
            onChange={handleChange}
            classNames={{ label: 'text-slate-500 text-xs' }}
          />
          <Input
            name="password"
            label="Password"
            description="Defaults to lowercaseCompanyName + 123! if blank"
            variant="bordered"
            radius="sm"
            value={formData.password || ''}
            onChange={handleChange}
            classNames={{
              label: 'text-slate-500 text-xs',
              description: 'text-slate-400',
            }}
          />
          <Input
            name="shopify_customer_id"
            label="Shopify Customer ID"
            variant="bordered"
            radius="sm"
            value={formData.shopify_customer_id || ''}
            onChange={handleChange}
            classNames={{ label: 'text-slate-500 text-xs' }}
          />
        </ModalBody>
        <ModalFooter className="pt-2">
          <Button
            variant="bordered"
            onClick={onClose}
            radius="sm"
            className="border-slate-300 text-slate-600"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSaveClick}
            radius="sm"
            disabled={isLoading}
            className="bg-airlock-purple font-medium"
          >
            {isLoading ? 'Creating...' : 'Create Partner'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddPartnerModal;
